import { useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { useRef } from 'react';
import { useEffect } from 'react';
import { CommentBox } from './CommentBox';
import { CommentViewer } from './CommentViewer';

import type { loader } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import { LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import type {
    CommentSubmitPayload,
    Comment,
} from '@modules/build-hub-comment/types';

import { AnimatePresence } from 'framer-motion';
import { useUser } from '@modules/user';
import { LoaderCircle } from 'lucide-react';

const POLL_INTERVAL = 5000;

interface BuildHubCommentsProps {
    itemId: number;
}

export const Comments = ({ itemId }: BuildHubCommentsProps) => {
    const [optimisticComments, setOptimisticComments] = useState<Comment[]>([]);
    const commentsFetcher = useFetcher<typeof loader>();

    const user = useUser();
    const me = user.__typename === 'Me' ? user : undefined;

    const scrollRef = useRef<HTMLDivElement>(null);

    // fetch + poll
    useEffect(() => {
        if (!itemId) return;

        const url = `${LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE}/${itemId}`;
        commentsFetcher.load(url);

        const interval = setInterval(() => {
            commentsFetcher.load(url);
        }, POLL_INTERVAL);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    const handlePosted = (payload: CommentSubmitPayload) => {
        // Create optimistic comment using fetcher.formData
        const optimisticComment: Comment = {
            id: Date.now(), // Use timestamp as temporary ID
            message: payload.message,
            createdAt: new Date().toISOString(),
            editedAt: new Date().toISOString(),
            link: '',
            author: {
                id: user.ID,
                name: me?.Name ?? '',
                email: me?.Email ?? '',
                avatar: me?.MemberImage?.LargeAvatar ?? '',
            },
            mentions: payload.entities
                .filter((e) => e.type === 'user' || e.type === 'owner')
                .map((e) => ({
                    id: Number(e.id),
                    name: e.label,
                    email: '',
                })),
            files: payload.entities
                .filter((e) => e.type === 'file')
                .map((e) => ({
                    id: Number(e.id),
                    title: e.label,
                })),
        };

        setOptimisticComments((prev) => [...prev, optimisticComment]);
    };

    const serverComments: Comment[] = commentsFetcher.data?.comments ?? [];

    const allComments = [
        ...serverComments,
        ...optimisticComments.filter((optComment) => {
            // Check if there's a server comment from the same user with the same message
            const hasMatchingServerComment = serverComments.some(
                (serverComment) =>
                    serverComment.author.id === optComment.author.id &&
                    serverComment.message === optComment.message
            );

            // Keep optimistic comment if no matching server comment found
            return !hasMatchingServerComment;
        }),
    ];

    // scroll to bottom when comments change
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [allComments.length]);

    const isLoading =
        commentsFetcher.state === 'loading' && !commentsFetcher.data;

    return (
        <div className="flex h-full flex-col">
            <div className="min-h-0 flex-1">
                <div
                    ref={scrollRef}
                    className="h-full space-y-2 overflow-y-auto"
                >
                    {isLoading ? (
                        <div className="flex h-full items-center justify-center pb-4">
                            <LoaderCircle className="size-4 animate-spin" />
                        </div>
                    ) : (
                        <AnimatePresence initial={false}>
                            {allComments.length === 0 && (
                                <div className="flex h-full items-center justify-center pb-4">
                                    <p className="text-sm text-gray-400">
                                        {`Be the first to leave a comment`}
                                    </p>
                                </div>
                            )}
                            {allComments.map((c) => {
                                return (
                                    <CommentViewer key={c.id} comments={[c]} />
                                );
                            })}
                        </AnimatePresence>
                    )}
                </div>
            </div>
            <div className="flex-shrink-0 border-t border-gray-200 pb-4 pt-4">
                <CommentBox
                    documentId={String(itemId)}
                    onPosted={handlePosted}
                />
            </div>
        </div>
    );
};
